<template>
  <div :class="$style.SettingPane__Content">
    <template v-for="item in config">
      <template v-if="activeTab === SettingTab.PREFERENCE">
        <div
          :key="item?.name"
          :class="[
            $style.SettingPane__Group,
            $style[`SettingPane__Group--${item?.name}`],
          ]"
          v-if="item?.name === 'orderConfirm'"
        >
          <h4 :class="$style.SettingPane__Title">{{ $t(item?.title) }}</h4>
          <slot name="preference" />
        </div>
      </template>
      <template v-else-if="activeTab === SettingTab.STYLE">
        <div
          :key="item?.name"
          :class="[
            $style.SettingPane__Group,
            $style[`SettingPane__Group--${item?.name}`],
          ]"
          v-if="['theme', 'layout'].includes(item?.name)"
        >
          <template v-if="item?.name === 'theme'">
            <h4 :class="$style.SettingPane__Label">{{ $t(item?.title) }}</h4>
            <ThemeSwitch
              :theme="setting?.theme"
              @onChange="v => $emit('onChange', { theme: v })"
            />
          </template>
          <template v-else-if="item?.name === 'layout'">
            <h4 :class="$style.SettingPane__Title">{{ $t(item?.title) }}</h4>
            <div :class="$style.SettingPane__ModuleWrapper" v-if="setting?.layout === LAYOUT_ENUM.ADVANCED">
              <ModuleSwitch
                :theme="setting?.theme"
                :name="moduleItem.name"
                :show="moduleItem.show"
                @onChange="handleModuleChange(moduleItem.value, $event)"
                v-for="moduleItem in setting?.modules"
                :key="moduleItem.value"
              />
              <div
                v-if="project === PROJECT.MASTER && setting?.modules"
                :class="$style.SettingPane__ModuleBtn"
                @click="v => $emit('onChange', { reset: true })"
              >
                {{ $t('trade-navigator.reset') }}
              </div>
            </div>
            <div
              :class="{
                [$style.SettingPane__LayoutOption]: true,
                [$style['SettingPane__LayoutOption--drawer']]: project === PROJECT.CONTRACT,
              }"
            >
              <div :class="$style.SettingPane__LayoutPreview">
                <LayoutPreview
                  v-for="option in item.options"
                  :key="option.value"
                  :layout="option.value"
                  :theme="setting?.theme"
                  :label="$t(option.label)"
                  @onChange="v => $emit('onChange', { layout: v })"
                  :active="option.value === setting?.layout"
                />
              </div>
              <slot name="layoutConfig" />
            </div>
          </template>
          <!-- <div v-else>
            <ToggleSwitch
              :value="setting?.[item?.name]"
              @onChange="handleToggleChange"
            />
          </div> -->
        </div>
      </template>
    </template>

  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { PROJECT } from '~/frontend-shared/helper/util';
import LayoutPreview from '../TradeNavigator/components/LayoutPreview.vue';
import ThemeSwitch from '../TradeNavigator/components/ThemeSwitch.vue';
import ToggleSwitch from '../TradeNavigator/components/ToggleSwitch.vue';
import ModuleSwitch from '../TradeNavigator/components/ModuleSwitch.vue';
import { SETTING_CONFIG, LAYOUT_ENUM } from '../TradeNavigator/config';
import cloneDeep from 'lodash/cloneDeep';
import messages from '../TradeNavigator/i18n';

export const enum SettingTab {
  PREFERENCE,
  STYLE,
}

export default defineComponent({
  components: {
    LayoutPreview,
		ThemeSwitch,
		ModuleSwitch,
    ToggleSwitch,
  },
  i18n: {
		messages,
	},
  emits: ['onChange'],
  props: {
    setting: Object,
    activeTab: {
      validator(value) {
				return [SettingTab.PREFERENCE, SettingTab.STYLE].includes(value);
			},
			default: SettingTab.PREFERENCE,
    },
    project: {
			validator(value) {
				return [PROJECT.MASTER, PROJECT.CONTRACT].includes(value);
			},
			default: PROJECT.MASTER,
		},
  },
  setup(props, { emit }) {
    const modules = ref(cloneDeep(props?.setting?.modules || {}));
    const modulesMap = computed(() => {
			return modules.value.reduce((map: any, item: any) => {
				const key = item.value;
				map[key] = item;
				return map;
			}, {});
		});

    return {
      PROJECT,
      LAYOUT_ENUM,
      SettingTab,
      config: SETTING_CONFIG.get(props?.project),
      handleModuleChange: (moduleName: string, show: boolean) => {
        if (!modulesMap.value[moduleName]) return;

        modulesMap.value[moduleName].show = show;

        emit('onChange', {
          // 当前变动模块
          module: modulesMap.value[moduleName],
        });
      },
      handleToggleChange(key: string, value: string) {
        emit('onChange', { [key]: value });
      },
    };
  },
});
</script>

<style lang="less" module>
.SettingPane {
  &__Content {
    background-color: var(--TradeSetting-background);
		color: var(--TradeSetting-text-color);
	}
	&__Group {
		display: flex;
		padding: 16px 0;
		flex-wrap: wrap;
		justify-content: space-between;
		align-items: center;
		&:not(:first-child) {
			border-top: 1px solid var(--TradeSetting-divide);
		}
    &--orderConfirm {
      flex-direction: column;
      align-items: flex-start;
    }
	}
	&__Label {
		font-size: 14px;
		font-weight: 400;
	}
  &__Title {
    font-size: 12px;
    color: var(--TradeSetting-secondary-color);
    font-weight: 400;
  }

	&__ModuleWrapper {
		padding-top: 4px;
		width: 100%;
	}

	&__ModuleBtn {
		margin-top: 4px;
		padding: 7px;
		font-size: 14px;
		line-height: 18px;
		border-radius: 2px;

		text-align: center;
		border: 1px solid var(--TradeSetting-Btn-border);
		color: var(--TradeSetting-Btn-text);
		cursor: pointer;
	}

	&__LayoutOption {
		display: flex;
		margin-top: 10px;
		width: 100%;
    &--drawer {
      padding: 0 10px;
      flex-direction: column;
    }
	}
  &__LayoutPreview {
    display: flex;
    width: 100%;
    justify-content: space-between;
  }
}
</style>
